import BasicSection from 'components/BasicSection';
import DataList from 'components/DataList';
import LinkButton from 'components/LinkButton';
import PanelContainer from 'components/PanelContainer';
import ProfileContentLoader from 'components/ProfileContentLoader';
import ProfileHeader from 'components/ProfileHeader';

import ReadMore from 'components/ReadMore';
import {ANIMATION_TIMEOUT_APPEAR} from 'constants/animation.js';
import {ENDPOINT} from 'constants/endpoints';
import {CONNECT_STATUS, POSITION_FLAG} from 'constants/profile';
import {ROUTE, ROUTE_REGEXP} from 'constants/route';
import {DateHelpers} from 'helpers/date-helpers';

import {Permission, Role} from 'helpers/user';
import {useApi} from 'hooks/useApi';
import PageLayout from 'layouts/PageLayout';
import uniqueId from 'lodash/uniqueId';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {CSSTransition} from 'react-transition-group';
import CandidateProfileContainer from 'scenes/CandidateProfile';
import ProfileItemEducation from 'scenes/CandidateProfile/components/ProfileItemEducation';
import ProfileItemPosition from 'scenes/CandidateProfile/components/ProfileItemPosition';
import ProfileItemReference from 'scenes/CandidateProfile/components/ProfileItemReference';
import ProfileItemTestResults from 'scenes/CandidateProfile/components/ProfileItemTestResults';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';
import Sidebar from 'scenes/Sidebar';
import AdditionalFiles from 'scenes/Sidebar/components/AdditionalFiles';
import ApplicationWidget from 'scenes/Sidebar/components/ApplicationWidget';
import CandidateInterviewDataWidget from 'scenes/Sidebar/components/CandidateInterviewDataWidget';
import CandidateProfileViewed from 'scenes/Sidebar/components/CandidateProfileViewed';
import CommunicationsWidget from 'scenes/Sidebar/components/CommunicationsWidget';
import Connections from 'scenes/Sidebar/components/Connections';
import ProfileTagInformation from 'scenes/Sidebar/components/ProfileTagInformation';
import ProfileViews from 'scenes/Sidebar/components/ProfileViews';
import RatingInformation from 'scenes/Sidebar/components/RatingInformation';
import ReviewsWidget from 'scenes/Sidebar/components/ReviewsWidget';
import UpdatedProfile from 'scenes/Sidebar/components/UpdatedProfile';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import EmailHistory from '../../scenes/Sidebar/components/EmailHistory';
import CandidateNotesWidget from '../../scenes/Sidebar/components/CandidateNotesWidget';
import JotformWidget from '../../scenes/Sidebar/components/JotformWidget';
import CandidateLinkedInSection from '../CandidateLinkedInSection';

import action from './actions';
import './styles.css';

const CandidateProfile = props => {
  const {
    user,
    history,
    location: { pathname, search },
    match: {
      params: { id },
    },
  } = props;
  const api = useApi();

  const [state, setState] = useState({
    init: false,
    profile: {},
  });

  const widgetTags = useRef();
  const widgetRatings = useRef();
  const widgetApply = useRef();
  const widgetAddFiles = useRef();
  const widgetCommunications = useRef();

  const [
    isCandidate,
    isRecruiter,
    isRecruiterDefault,
    isRecruiterGbs,
    isRecruiterPremiumRecFirm,
    isAllowedEditCandidateProfile,
  ] = useMemo(
    () => [
      Role.isCandidate(user),
      Role.isRecruiter(user),
      Role.isRecruiterDefault(user),
      Role.isRecruiterGbs(user),
      Role.isRecruiterPremiumRecFirm(user),
      Permission.isAllowedEditCandidateProfile(user.permissions),
    ],
    [user]
  );

  const loadData = () => {
    setState(prevState => ({
      ...prevState,
      init: false,
    }));
    api.get(
      ENDPOINT.ENTITY.CANDIDATE,
      { id },
      {
        handleForbidden: () => history.replace(ROUTE.ERROR_404),
        handleNotFound: () => history.replace(ROUTE.ERROR_404),
        handleSuccess: profile => {
          setState({
            init: true,
            profile: { ...profile },
          });

          if (search) {
            const params = new URLSearchParams(search);
            const act = params.get('action');

            history.replace(pathname);

            if (
              'message' === act &&
              (profile.Profile === 'Full' ||
                profile.ConnectStatus === CONNECT_STATUS.CONNECTED)
            ) {
              action.sendMessage(api, history, user, {
                id,
                FullName: profile.FullName,
                enableTemplates: profile.Profile === 'Full',
              });
            }
          }
        },
      }
    );
  };

  useEffect(() => {
    loadData();
  }, [pathname]);

  const reloadTagsWidget = () => {
    widgetTags.current && widgetTags.current.reload();
  };

  const reloadApplyWidget = () => {
    widgetApply.current && widgetApply.current.reload();
  };

  const reloadFilesWidget = () => {
    widgetAddFiles.current && widgetAddFiles.current.reload();
  };

  const reloadCommunicationsWidget = () => {
    widgetCommunications.current && widgetCommunications.current.reload();
  };

  const handleConnect = () => action.connect(api, id, { state, setState });
  const handleDisconnect = () =>
    action.disconnect(api, id, { state, setState });
  const handleAccept = () => action.accept(api, id, { state, setState });
  const handleReject = () => action.reject(api, id, { state, setState });
  const handleMessage = () =>
    action.sendMessage(api, history, user, {
      id,
      FullName: state.profile.FullName,
      enableTemplates: state.profile.Profile === 'Full',
    });

  const handleRate = () =>
    action.rate(api, user, id, {
      state,
      setState,
      callback: widgetRatings.current && widgetRatings.current.reload,
    });

  const handleTag = () =>
    action.tag(api, user, id, {
      state,
      callback: reloadTagsWidget,
    });

  const handleBeforeSetFirmRating = () => action.beforeSetFirmRating(api, user);

  const handleSetFirmRating = ({ FirmID }, mark) =>
    action.setFirmRating(state, api, user, { id: FirmID, mark }, { setState });

  const handleDownloadResume = () => action.downloadResume(id);

  const handleApplyCandidateToPosition = () =>
    action.apply(id, reloadApplyWidget);

  const handleAddFiles = () => action.addFiles(id, reloadFilesWidget);

  const handleAddToShortlist = () =>
    action.addToShortlist(id, state.profile.FullName, history);

  const handleSendInterviewQuestionsEmail = () =>
    action.sendInterviewQuestionsEmail(state, api, id, {
      setState,
      callback: reloadCommunicationsWidget,
    });

  const handleSendReview = () => {
    action.sendReview(id, reloadCommunicationsWidget);
  };

  const handleUpdateResume = () => {
    action.sendUpdateResumeEmail(api, id, reloadCommunicationsWidget);
  };

  const handleSendGAT = () => {
    action.sendGATEmail(api, id, reloadCommunicationsWidget);
  };

  const handleSendJob = () => {
    let message = '';
    if (
      state.profile.IsRecruiter === 'Yes' &&
      state.profile.PrivacyOpt7 === 'Yes'
    ) {
      message =
        'This user is a client of GoBuyside and has job notifications turned off, \nare you sure you want to send Job to this user?';
    } else if (state.profile.IsRecruiter === 'Yes') {
      message =
        'This user is a client of GoBuyside, are you sure you want to send Job to this user?';
    } else if (state.profile.PrivacyOpt7 === 'Yes') {
      message =
        'This user has job notifications turned off, are you sure you want to send Job to this user?';
    }

    if (message) {
      ConfirmDialog({
        dismissWhenClose: true,
        bodyRender: <div style={{ whiteSpace: 'pre' }}>{message}</div>,
        textSuccessBtn: 'Yes',
        textCancelBtn: 'Cancel',
      }).then(function () {
        action.sendJob(id, reloadCommunicationsWidget);
      });
    } else {
      action.sendJob(id, reloadCommunicationsWidget);
    }
  };

  const handleLockProfile = lock => {
    action.lockProfile(id, lock, api, response => {
      setState(state => ({
        ...state,
        profile: { ...state.profile, Lock: { ...response } },
      }));
    });
  };

  /* Render Blocks  */

  const renderHeader = useMemo(() => {
    if (state.init) {
      const {
        Rated,
        FullName,
        PictureUrl,
        EmailAddress,
        ResumeUpdateData,
        Resume,
        Confidential = {},
        Positions: [position],
        ConnectStatus,
        Resumes,
        Profile,
        InterviewQuestionsEmailSentDate,
        InterviewRequestSentDate,
        ShowInterviewQuestionsEmailInterviewButton,
        ShowSendGatButton,
        Lock,
      } = state.profile;

      const { EmailAddress2, PhoneNumber1, PhoneNumber2 } = Confidential;

      const isFullProfile = Profile === 'Full';

      let actions = {
        onSetFirmRating: handleSetFirmRating,
        onBeforeSetFirmRating: handleBeforeSetFirmRating,
      };

      if (isCandidate) {
        actions = {
          ...actions,
          onMessage:
            CONNECT_STATUS.CONNECTED === ConnectStatus ? handleMessage : null,
          onConnect: handleConnect,
          onDisconnect: handleDisconnect,
          onAccept: handleAccept,
          onReject: handleReject,
        };
      }

      if (isFullProfile) {
        if (isRecruiter) {
          actions = {
            ...actions,
            onAddToShortlist: Permission.isAllowedCandidatesShortlists(
              user.permissions
            )
              ? handleAddToShortlist
              : null,
          };

          if (!isRecruiterDefault) {
            actions = {
              ...actions,
              onMessage: handleMessage,
              onRate: handleRate,
              onDownloadResume: handleDownloadResume,
              onApply: Permission.isAllowedManageJobs(user.permissions)
                ? handleApplyCandidateToPosition
                : null,
            };
          }
        }

        if (isRecruiterGbs) {
          actions = {
            ...actions,
            onLockProfile: handleLockProfile,
            onSchedule: (recruiterId) => {
              api.post(ENDPOINT.ACTION.SEND_INTERVIEW_REQUEST, {
                candidateId: id,
                recruiterId,
              }, {
                handleSuccess: (response) => {
                  BasicDialog({
                    bodyRender: 'Interview request has been sent successfully.',
                    withoutButtons: true,
                  });
                  setState({
                    ...state,
                    profile: {
                      ...state.profile,
                      InterviewRequestSentDate: response.InterviewRequestSentDate,
                    }
                  })
                  // loadData();
                },
                handleError: () => {
                  BasicDialog({
                    bodyRender: 'Something went wrong. Please try again later.',
                    withoutButtons: true,
                  });
                }
              });
            },
          };
        }

        if (isRecruiterPremiumRecFirm || isRecruiterGbs) {
          actions = {
            ...actions,
            onTagProfile: handleTag,
            onAddFile: Permission.isAllowedCandidateProfileAttachments(
              user.permissions
            )
              ? handleAddFiles
              : null,
          };
        }

        if (Permission.isAllowedSendCandidateNotify(user.permissions)) {
          if (ShowInterviewQuestionsEmailInterviewButton) {
            actions.onSendInterviewQuestions = handleSendInterviewQuestionsEmail;
          }

          if (ShowSendGatButton) {
            actions.onSendGAT = handleSendGAT;
          }

          actions.onUpdateResume = handleUpdateResume;
          actions.onSendReview = handleSendReview;
          actions.onSendJob = handleSendJob;
        }

        if (isAllowedEditCandidateProfile) {
          actions = {
            ...actions,
            onEditProfile: () => {
              history.push(
                ROUTE_REGEXP.CANDIDATE_PROFILE_EDITOR.replace(':id(\\d+)', id)
              );
            },
          };
        }
      }

      const resumeHistory = Resumes && !!Resumes.length && (
        <div className="profile-header__old-resumes">
          <p
            className="profile-header__text"
            style={{
              margin: '0',
            }}
          >
            Old Resumes
          </p>

          {Resumes.map(item => (
            <span className="profile-header__old-resume" key={item.id}>
              <LinkButton
                text={item.date}
                btnFontFamily="Noto Sans, sans-serif"
                btnFontWeight="400"
                btnFontSize="0.8571rem"
                controlFunc={() => action.downloadResume(id, item.id)}
              />
            </span>
          ))}
        </div>
      );

      return (
        <ProfileHeader
          isContactInformationHidden={!isFullProfile}
          isAddReviewBtnHidden
          resumeHistory={resumeHistory}
          data={{
            FullName,
            PictureUrl,
            EmailAddress,
            ConnectStatus,
            ResumeUpdateData,
            ResumeUrl: Resume,
            EmailAddress2,
            PhoneNumber1,
            PhoneNumber2,
            position,
            Rated,
            InterviewQuestionsEmailSentDate,
            InterviewRequestSentDate,
            Lock,
          }}
          actions={actions}
        />
      );
    }

    return <ProfileContentLoader />;
  }, [state.profile, state.init, isRecruiterGbs]);

  const renderExperience = useMemo(() => {
    if (state.init) {
      const { Positions } = state.profile;
      const experience =
        Positions && Positions.length > 0
          ? Positions.filter(
              (position) =>
                position.flag === POSITION_FLAG.POSITION
            )
          : null;

      return (
        experience &&
        experience.length > 0 && (
          <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
            <BasicSection title="Experience">
              {experience.map(position => (
                <ProfileItemPosition
                  isAddReviewBtnHidden
                  data={position}
                  actions={{
                    onSetFirmRating: handleSetFirmRating,
                    onBeforeSetFirmRating: handleBeforeSetFirmRating,
                  }}
                  key={position.PositionID}
                />
              ))}
            </BasicSection>
          </PanelContainer>
        )
      );
    }
  }, [state.profile, state.init]);

  const renderEducation = useMemo(() => {
    if (state.init) {
      const { Positions } = state.profile;

      const education =
        Positions && Positions.length > 0
          ? Positions.filter(
              (position, index) =>
                index !== 0 &&
                position.flag === POSITION_FLAG.EDUCATION &&
                Number(position.Current) !== 1
            )
          : null;

      return (
        education &&
        education.length > 0 && (
          <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
            <BasicSection title="Education">
              {education.map(position => (
                <ProfileItemEducation
                  data={position}
                  key={position.PositionID}
                />
              ))}
            </BasicSection>
          </PanelContainer>
        )
      );
    }
  }, [state.profile, state.init]);

  const renderAcademics = useMemo(() => {
    if (state.init && state.profile.Confidential) {
      const {
        Confidential: {
          UndergraduateMajor,
          GmatName,
          GpaName,
          MathSatName,
          ScoreName,
          VerbalSatName,
          WritingSatName,
        },
      } = state.profile;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection title="Academics">
            <div
              className="candidate-profile-page__text"
              style={{ margin: '15px 0' }}
            >
              <DataList
                objectList={{
                  'Undergraduate Major': UndergraduateMajor,
                  GPA: GpaName,
                  'SAT (M/V/W)': `${MathSatName}/${VerbalSatName}/${WritingSatName}`,
                  'ACT Score': ScoreName,
                  GMAT: GmatName,
                }}
              />
            </div>
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile, state.init]);

  const renderSummary = useMemo(() => {
    if (state.init && state.profile.Summary) {
      const { Summary } = state.profile;
      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection title="Summary">
            <div
              className="candidate-profile-page__text"
              style={{ margin: '15px 0' }}
            >
              {Summary && (
                <ReadMore text={Summary} readMoreCharacterLimit={350} />
              )}
            </div>
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile, state.init]);

  const renderPreferences = useMemo(() => {
    if (state.init && state.profile.Preferences) {
      const {
        Preferences: { buyside, location, fundsize },
      } = state.profile;

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection title="Preferences">
            <DataList
              objectList={{
                'Investment Strategy Interests': buyside,
                Location: location,
                'Fund Size': fundsize,
              }}
            />
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile, state.init]);

  const renderPersonalInformation = useMemo(() => {
    if (state.init && state.profile.ManagePersonalAccount) {
      const {
        profile: {
          ManagePersonalAccount,
          ImmediateStartOpportunities,
          Languages,
          Citizenship,
          JobsEmails,
          ProjectsEmails,
        },
      } = state;

      const objectList = {
        'Manage Account': ManagePersonalAccount,
        'Immediate Start': ImmediateStartOpportunities,
        Citizenship,
      };

      if (Languages && Languages.length) {
        objectList['Foreign Languages:'] = Object.values(Languages).map(
          language => `${language.LanguageName} (${language.LevelName})`
        );
      }
      if (JobsEmails) {
        objectList['Jobs Emails:'] = JobsEmails;
      }
      if (ProjectsEmails) {
        objectList['Projects Emails:'] = ProjectsEmails;
      }

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection title="Personal Information">
            <DataList objectList={objectList} />
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile, state.init]);

  const renderReferences = useMemo(() => {
    if (
      state.init &&
      state.profile.References &&
      state.profile.References.length
    ) {
      const { References } = state.profile;
      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection title="References">
            {References && References.length > 0 && (
              <div className="candidate-profile-page__reference-list">
                {References.map(record => (
                  <ProfileItemReference
                    data={record}
                    action={{}}
                    key={uniqueId()}
                  />
                ))}
              </div>
            )}
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile, state.init]);

  const renderAptitudeTests = useMemo(() => {
    if (state.init && state.profile.AptitudeTests) {
      const { AptitudeTests } = state.profile;
      const aptitudeTests = AptitudeTests ? Object.values(AptitudeTests) : null;

      return (
        aptitudeTests &&
        aptitudeTests.length > 0 && (
          <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
            <BasicSection title="GoBuyside Aptitude Test">
              <div className="candidate-profile-page__test-results-list">
                {aptitudeTests.map(item => (
                  <ProfileItemTestResults
                    data={item}
                    showPercentile={true}
                    user={props.user}
                    history={props.history}
                    key={uniqueId()}
                  />
                ))}
              </div>
            </BasicSection>
          </PanelContainer>
        )
      );
    }
  }, [state.profile, state.init]);

  const renderFiles = useMemo(() => {
    if (
      state.init &&
      state.profile.Files &&
      Object.keys(state.profile.Files).length > 0
    ) {
      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection title="Candidate's Work">
            <div className="candidate-profile-page__files">
              {Object.keys(state.profile.Files).map((key, index) => {
                const files = state.profile.Files[key];
                return (
                  !!files.length && (
                    <div
                      className="candidate-profile-page__file-list-block"
                      key={index}
                    >
                      <h2 className="candidate-profile-page__file-list-title">
                        {key}
                      </h2>
                      <div className="candidate-profile-page__file-list">
                        {files.map(file => (
                          <div
                            key={file.FileID}
                            className="candidate-profile-page__file-list--item"
                          >
                            <LinkButton
                              controlFunc={() => action.downloadFile(file)}
                              text={file.FileName}
                              underline={false}
                            />
                          </div>
                        ))}
                        <div className="candidate-profile-page__file-list--item">
                          <LinkButton
                            controlFunc={() =>
                              action.downloadAllFiles(files[0])
                            }
                            text="Download all"
                          />
                        </div>
                      </div>
                    </div>
                  )
                );
              })}
            </div>
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile, state.init]);

  const renderActivity = useMemo(() => {
    if (
      state.init &&
      state.profile.Activity &&
      (isRecruiterGbs || isRecruiterPremiumRecFirm)
    ) {
      let Activity = {};
      if (state.profile.Activity.lastActivityDate) {
        Activity['Last Activity'] = DateHelpers.getMonthNameDayYearFormat(
          state.profile.Activity.lastActivityDate
        );
      }

      return (
        <PanelContainer styles={{ marginBottom: '10px', paddingTop: '25px' }}>
          <BasicSection title="Activity">
            <DataList
              objectList={{
                ...Activity,
                '12 months': state.profile.Activity.lastYearActivity,
                '6 months': state.profile.Activity.last6MonthActivity,
                '3 months': state.profile.Activity.last3MonthActivity,
                '1 month': state.profile.Activity.lastMonthActivity,
              }}
            />
          </BasicSection>
        </PanelContainer>
      );
    }
  }, [state.profile, state.init]);

  const sideBar = useMemo(() => {
    if (state.init) {
      return (
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Sidebar>
            {state.profile.Profile === 'Full' && (
              <>
                {isRecruiterPremiumRecFirm && (
                  <CandidateNotesWidget candidateId={id} />
                )}
                {isRecruiter && (
                  <RatingInformation
                    id={id}
                    user={user}
                    ref={widgetRatings}
                  />
                )}
                {isRecruiterPremiumRecFirm && (
                  <>
                    <ApplicationWidget id={id} isJob ref={widgetApply} />
                    <ApplicationWidget id={id} isProject />
                    <CommunicationsWidget id={id} ref={widgetCommunications} />
                    <AdditionalFiles id={id} ref={widgetAddFiles} />
                  </>
                )}
                {isRecruiter && (
                  <CandidateInterviewDataWidget
                    data={state.profile.InterviewData}
                  />
                )}
                {Permission.isAllowedTeamProfileViews(user.permissions) && (
                  <ProfileViews id={id} />
                )}
                {isRecruiterGbs && <UpdatedProfile id={id} />}

                {isRecruiterPremiumRecFirm && (
                  <JotformWidget candidateId={id} />
                )}
                {isRecruiterPremiumRecFirm && (
                  <>
                    <ReviewsWidget id={id} user={user} history={history} />
                    <ProfileTagInformation id={id} ref={widgetTags} />
                    <CandidateProfileViewed id={id} />
                    <EmailHistory candidateId={id} />
                    <AdditionalFiles id={id} ref={widgetAddFiles} />
                  </>
                )}
              </>
            )}
            <Connections id={id} user={user} />
          </Sidebar>
        </CSSTransition>
      );
    }
  }, [state.init, id]);

  return (
    <PageLayout
      pageClass="candidate-profile-page"
      pageTitle={state.init ? state.profile.FullName : 'Candidate Profile'}
      headerContent={renderHeader}
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <CandidateProfileContainer>
            {isRecruiterPremiumRecFirm && (
              <CandidateLinkedInSection candidateId={id} />
            )}
            {renderExperience}
            {renderSummary}
            {renderEducation}
            {renderAcademics}
            {renderPreferences}
            {renderPersonalInformation}
            {renderReferences}
            {renderAptitudeTests}
            {renderFiles}
            {renderActivity}
          </CandidateProfileContainer>
        </CSSTransition>
      }
      sidebarContent={sideBar}
    />
  );
};

export default CandidateProfile;
